// This file is responsible for making and caching requests to the Google Maps Routes API.
//  It returns the snapped coordinates and road-aligned path for a given route.
//
// Ambassador: Yatharth.
//
import _ from 'lodash';
import { useCallback } from 'react';

import { latLngNumberFromLatLngStr } from './googleMapTypes';
import { LatLngStr, Route } from './stopTypes';

// Global cache for all Routes API requests.
const cache: Record<string, google.maps.routes.Route> = {};

export const useFetchRoute = ({ maps }: { maps: typeof google.maps | null }) => {
    //

    // Note: `google.maps.DirectionsService` is deprecated (as of February 2026) in
    //  favor of `google.maps.routes.Route.computeRoutes`, which is what we use here.
    const fetchRoute = useCallback(
        (route: Route, callback: (result: google.maps.routes.Route) => void) => {
            //

            // Check cache.
            const cacheKey = JSON.stringify(route);
            if (cacheKey in cache) {
                callback(cache[cacheKey]);
                return;
            }

            // If Google Maps is not ready, we can't do anything.
            if (maps === null) {
                return;
            }

            // Otherwise, prepare a compute-routes request.
            const origin = latLngNumberFromLatLngStr(route[0]);
            const destination = latLngNumberFromLatLngStr(route[route.length - 1]);
            const intermediates = route.slice(1, route.length - 1).map((stop) => ({
                location: latLngNumberFromLatLngStr(stop),
            }));
            const request: google.maps.routes.ComputeRoutesRequest = {
                origin,
                destination,
                intermediates,
                travelMode: 'DRIVING',
                // Only fetch the fields we use: the polyline path for drawing the route,
                //  and the legs for snapping pins to the route.
                fields: ['path', 'legs'],
            };

            // Issue the request, and cache the result.
            void (async () => {
                try {
                    // The routes library loads on demand; repeated calls are no-ops.
                    const { Route: RouteClass } = await maps.importLibrary('routes');
                    const { routes } = await RouteClass.computeRoutes(request);
                    const result = routes?.[0];
                    if (!result) {
                        return;
                    }
                    cache[cacheKey] = result;
                    callback(result);
                } catch (error) {
                    console.warn('Failed to compute route:', error);
                }
            })();

            //
        },
        [maps],
    );

    return fetchRoute;
};

// Get the snapped coordinates of the pin markers for a given route result.
//  An entry is null when the Routes API did not return a location for that stop;
//  callers should fall back to the stop's original coordinates.
export const snappedLatLngsFromRoute = (
    routeResult: google.maps.routes.Route,
): (LatLngStr | null)[] => {
    const legs = routeResult.legs ?? [];
    const startsOfEachLeg = _.map(legs, (leg) => leg.startLocation);
    const endsOfEachLeg = _.map(legs, (leg) => leg.endLocation);
    const legMarkers = [startsOfEachLeg[0], ...endsOfEachLeg];
    return legMarkers.map((legMarker) =>
        legMarker
            ? {
                  lat: legMarker.lat.toString(),
                  lng: legMarker.lng.toString(),
              }
            : null,
    );
};
